/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business PJ Sandbox のチケット一括取込で使う純関数。貼り付けテキストの案件番号への分割、ブロック名（案件番号下6桁_案件名）の組み立て、既存ブロックと重複する案件の除外、Backlog 課題 URL の生成
 * @graph-connects none
 */
import { BACKLOG_BASE } from '../ChildTicketsModal.js';
import type { TicketLookupItem } from '../../../../types/project.js';
import type { BlockKind, SandboxBlock } from './types.js';

/** ブロック名の先頭に付ける案件番号の桁数（案件番号の下 N 桁）。 @graph-connects none */
export const BLOCK_NAME_NUMBER_DIGITS = 6;

/**
 * 貼り付けテキストを案件番号のトークン列へ分割する。
 *
 * Google スプレッドシートの 1 列をそのままコピーすると改行区切りで貼られるが、
 * 横方向のコピー（タブ区切り）やカンマ・読点区切りで貼られることもあるため、
 * 区切り文字はまとめて受ける。空行は落とし、重複は先勝ちで 1 件に畳む。
 *
 * @graph-connects none
 */
export function parseTicketTokens(text: string): string[] {
  const raw = text.split(/[\s,、;；]+/);
  const seen = new Set<string>();
  const tokens: string[] = [];
  for (const token of raw) {
    const trimmed = token.trim();
    if (trimmed === '' || seen.has(trimmed)) continue;
    seen.add(trimmed);
    tokens.push(trimmed);
  }
  return tokens;
}

/**
 * 案件番号の下 6 桁を取り出す。6 桁未満はそのまま返す。
 * @graph-connects none
 */
export function shortIssueNumber(issueNumber: string): string {
  return issueNumber.length <= BLOCK_NAME_NUMBER_DIGITS
    ? issueNumber
    : issueNumber.slice(-BLOCK_NAME_NUMBER_DIGITS);
}

/**
 * 取込ブロックの表示名を「案件番号の下6桁_案件名」で組み立てる。
 * @graph-connects none
 */
export function ticketBlockName(issueNumber: string, summary: string): string {
  return `${shortIssueNumber(issueNumber)}_${summary}`;
}

/**
 * 案件番号を手入力するときに補う既定のプロジェクトキー。
 * PMO が扱う案件母票は全て Backlog の AIRCLOSET プロジェクトに起票されるため、
 * 数字だけを入力されたらこの接頭辞を補って課題キーにする。
 * @graph-connects none
 */
export const DEFAULT_ISSUE_PROJECT_KEY = 'AIRCLOSET';

/**
 * 手入力の案件番号を Backlog 課題キーへ正規化する。
 *
 * `AIRCLOSET-137048` / `aircloset-137048` / `137048`（既定プロジェクトを補う）/
 * Backlog 課題 URL を受け付ける。空入力は「課題キーなし」を意味する null、
 * 解釈できない入力は不正を示す undefined を返し、呼び出し側が保存を止められるようにする。
 *
 * @graph-connects none
 */
export function normalizeIssueKeyInput(input: string): string | null | undefined {
  const trimmed = input.trim();
  if (trimmed === '') return null;
  const candidate = (trimmed.split(/[?#]/)[0]?.split('/').pop() ?? '').trim().toUpperCase();
  if (/^[A-Z][A-Z0-9_]*-\d+$/.test(candidate)) return candidate;
  if (/^\d+$/.test(candidate)) return `${DEFAULT_ISSUE_PROJECT_KEY}-${candidate}`;
  return undefined;
}

/**
 * Backlog 課題 URL。API が返す backlogUrl があればそれを優先し、
 * 手入力の案件番号（API を経由しないブロック）は課題キーから組み立てる。
 * @graph-connects none
 */
export function backlogIssueUrl(issueKey: string): string {
  return `${BACKLOG_BASE}${issueKey}`;
}

/** チケット取込で作る未配置ブロックの素（配置・ID は呼び出し側が付ける）。 @graph-connects none */
export interface TicketBlockDraft {
  name: string;
  sizePt: number;
  kind: BlockKind;
  issueKey: string;
}

/**
 * 照会結果 1 件を未配置ブロックの素へ変換する。
 * ざっくり見積が未入力の案件は 0pt で作り、白板上で編集して埋められるようにする
 * （取込自体を落とすと、見積前の案件を並べられなくなるため）。
 * @graph-connects none
 */
export function toTicketBlockDraft(item: TicketLookupItem): TicketBlockDraft {
  return {
    name: ticketBlockName(item.issueNumber, item.summary),
    sizePt: item.roughPt ?? 0,
    kind: item.kind,
    issueKey: item.issueKey,
  };
}

/**
 * 照会結果のうち、実際にブロックを作るべきものだけを入力順で返す。
 *
 * 渡されたブロック（表示中の白板 + 全チーム共有の未配置トレイ）に既に同じ課題キーがあれば
 * 飛ばす。同じ案件番号を貼り直してもブロックが増殖せず、取込のやり直しが安全になる。
 * 未配置ブロックは全チーム共通の 1 プールなので、白板ごとの重複判定にはしない
 * （白板に配置済みの案件を、共有トレイへもう 1 枚取り込んでしまうのを防ぐ）。
 * 同じ照会結果に同一課題キーが 2 件あった場合も 1 件に畳む。
 *
 * @graph-connects none
 */
export function pendingTicketItems(
  blocks: readonly SandboxBlock[],
  items: readonly TicketLookupItem[],
): TicketLookupItem[] {
  const existing = new Set(blocks.flatMap((b) => (b.issueKey === undefined ? [] : [b.issueKey])));
  const pending: TicketLookupItem[] = [];
  for (const item of items) {
    if (existing.has(item.issueKey)) continue;
    existing.add(item.issueKey);
    pending.push(item);
  }
  return pending;
}
